import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

interface FeedbackContextValue {
  isVisible: boolean;
  message: string;
  showFeedback: (message: string) => void;
  hideFeedback: () => void;
}

const FeedbackContext = createContext<FeedbackContextValue | undefined>(undefined);

interface FeedbackProviderProps {
  children: React.ReactNode;
}

const FEEDBACK_DURATION_MS = 1500;
const FEEDBACK_OVERLAY_ID = 'global-feedback-overlay';

// Direct DOM manipulation for instant feedback - bypasses React render cycle
let feedbackTimer: NodeJS.Timeout | null = null;

function showFeedbackDOM(message: string) {
  // Clear any existing timer
  if (feedbackTimer) {
    clearTimeout(feedbackTimer);
  }
  
  let overlay = document.getElementById(FEEDBACK_OVERLAY_ID);
  
  if (!overlay) {
    // Create the overlay element if it doesn't exist
    overlay = document.createElement('div');
    overlay.id = FEEDBACK_OVERLAY_ID;
    overlay.style.cssText = `
      position: fixed;
      bottom: 72px;
      left: 0;
      right: 0;
      margin-left: auto;
      margin-right: auto;
      width: auto;
      max-width: calc(100% - 32px);
      padding: 0 16px;
      display: flex;
      justify-content: center;
      z-index: 1000;
      pointer-events: none;
    `;
    
    const inner = document.createElement('div');
    inner.id = `${FEEDBACK_OVERLAY_ID}-inner`;
    inner.style.cssText = `
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 12px;
      width: 100%;
      max-width: 90%;
      background: linear-gradient(135deg, var(--primary-colour) 0%, color-mix(in srgb, var(--primary-colour) 82%, white) 100%);
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-radius: 12px;
      padding: 0;
      box-shadow: 0 4px 16px color-mix(in srgb, var(--primary-colour) 50%, transparent), 0 2px 8px rgba(0, 0, 0, 0.3);
      overflow: hidden;
      min-height: 68px;
      box-sizing: border-box;
    `;
    
    // Checkmark icon (SVG)
    const icon = document.createElement('span');
    icon.style.cssText = `
      padding: 16px 0 16px 32px;
      display: flex;
      align-items: center;
      flex-shrink: 0;
    `;
    icon.innerHTML = `<svg style="width: 1.6rem; height: 1.6rem; color: white; filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>`;
    
    const text = document.createElement('span');
    text.id = `${FEEDBACK_OVERLAY_ID}-text`;
    text.style.cssText = `
      font-size: 0.75rem;
      font-weight: 700;
      color: white;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      white-space: nowrap;
      text-align: center;
      padding: 16px 32px 16px 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    `;
    
    inner.appendChild(icon);
    inner.appendChild(text);
    overlay.appendChild(inner);
    document.body.appendChild(overlay);
  }
  
  // Update the message text
  const textEl = document.getElementById(`${FEEDBACK_OVERLAY_ID}-text`);
  if (textEl) {
    textEl.textContent = message;
  }
  
  // Show the overlay
  overlay.style.display = 'flex';
  
  // Auto-hide after duration
  feedbackTimer = setTimeout(() => {
    if (overlay) {
      overlay.style.display = 'none';
    }
  }, FEEDBACK_DURATION_MS);
}

function hideFeedbackDOM() {
  if (feedbackTimer) {
    clearTimeout(feedbackTimer);
  }
  const overlay = document.getElementById(FEEDBACK_OVERLAY_ID);
  if (overlay) {
    overlay.style.display = 'none';
  }
}

// Export direct DOM function for maximum speed - bypasses React entirely
export function showFeedbackInstant(message: string) {
  showFeedbackDOM(message);
}

/**
 * Feedback provider component that manages feedback overlay state.
 * Uses direct DOM manipulation for instant feedback display.
 */
export const FeedbackProvider: React.FC<FeedbackProviderProps> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [message, setMessage] = useState('');

  const hideFeedback = useCallback(() => {
    hideFeedbackDOM();
    setIsVisible(false);
    setMessage('');
  }, []);

  const showFeedback = useCallback((msg: string) => {
    // Immediately show via DOM for instant feedback
    showFeedbackDOM(msg);
    
    // Also update React state for any components that need it
    setMessage(msg);
    setIsVisible(true);
    
    // Sync React state when hiding
    setTimeout(() => {
      setIsVisible(false);
      setMessage('');
    }, FEEDBACK_DURATION_MS);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      hideFeedbackDOM();
    };
  }, []);

  const value = useMemo(() => ({
    isVisible,
    message,
    showFeedback,
    hideFeedback,
  }), [isVisible, message, showFeedback, hideFeedback]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
};

/**
 * Hook to access feedback overlay functionality.
 * Must be used within a FeedbackProvider.
 */
export const useFeedback = (): FeedbackContextValue => {
  const context = useContext(FeedbackContext);
  if (context === undefined) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
};
